import React from "react";

import { ButtonStandard, type ButtonStandardProps } from "../../atoms/ButtonStandard";
import { Text } from "../../atoms/Text";
import { ModalBase, type ModalBaseProps } from "../ModalBase";

export interface ModalConfirmationProps extends Omit<ModalBaseProps, "children"> {
	/**
	 * Optional title displayed at the top of the modal
	 */
	title?: string;
	/**
	 * Optional subtitle displayed below the title
	 */
	subtitle?: string;
	/**
	 * Optional message content displayed in the main content area
	 */
	message?: string;
	/**
	 * Props for the negative action button. The label (children) and onClick handler are required.
	 * Other props will be merged with sensible defaults.
	 */
	negativeButtonProps: Partial<ButtonStandardProps> &
		Pick<ButtonStandardProps, "children" | "onClick"> & { "data-testid"?: string };
	/**
	 * Props for the positive action button. The label (children) and onClick handler are required.
	 * Other props will be merged with sensible defaults.
	 */
	positiveButtonProps: Partial<ButtonStandardProps> &
		Pick<ButtonStandardProps, "children" | "onClick"> & { "data-testid"?: string };
}

/**
 * ModalConfirmation component that provides a confirmation modal template.
 *
 * This component is built on top of ModalBase and provides a standardized
 * confirmation dialog with:
 * - Optional structured content (title, subtitle, message)
 * - X close button in the upper right corner
 * - Standardized positive and negative action buttons at the bottom
 * - Customizable button props with sensible defaults
 *
 * The positive action button defaults to green styling while the negative action
 * button defaults to blue/primary styling for clear visual distinction.
 */
const ModalConfirmation = (props: ModalConfirmationProps) => {
	const {
		title,
		subtitle,
		message,
		isOpen,
		onClose,
		positiveButtonProps,
		negativeButtonProps,
		dangerouslySetClassName,
	} = props;

	// Default props for negative button (typically "Cancel" style)
	const defaultNegativeButtonProps: Partial<ButtonStandardProps> = {
		variant: "solid",
		color: "primary",
		size: "large",
	};

	// Default props for positive button (typically "Confirm" style)
	const defaultPositiveButtonProps: Partial<ButtonStandardProps> = {
		variant: "solid",
		color: "green",
		size: "large",
	};

	// Merge provided props with defaults, including data-testid attributes
	const mergedNegativeButtonProps = {
		...defaultNegativeButtonProps,
		...negativeButtonProps,
		"data-testid": negativeButtonProps["data-testid"] || "modal-confirmation-negative",
	};
	const mergedPositiveButtonProps = {
		...defaultPositiveButtonProps,
		...positiveButtonProps,
		"data-testid": positiveButtonProps["data-testid"] || "modal-confirmation-positive",
	};

	return (
		<ModalBase isOpen={isOpen} onClose={onClose} dangerouslySetClassName={dangerouslySetClassName}>
			<div className="flex-1 px-6 pb-6">
				<div className="text-center">
					{title && (
						<Text size="sub-heading" weight="semi-bold">
							{title}
						</Text>
					)}
					{subtitle && (
						<Text size="paragraph-3" weight="regular" dangerouslySetClassName={title ? "mt-2" : ""}>
							{subtitle}
						</Text>
					)}
					{message && (
						<Text
							size="paragraph-1"
							weight="regular"
							dangerouslySetClassName={title || subtitle ? "mt-4" : ""}
						>
							{message}
						</Text>
					)}
				</div>
			</div>

			<div className="flex justify-center gap-2 px-6 pb-6">
				<div className="w-fit">
					<ButtonStandard {...mergedNegativeButtonProps} />
				</div>
				<div className="w-fit">
					<ButtonStandard {...mergedPositiveButtonProps} />
				</div>
			</div>
		</ModalBase>
	);
};

ModalConfirmation.displayName = "ModalConfirmation";

export { ModalConfirmation };
